/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * CIRCULAR SPECTRUM — Radial frequency display
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * Frequency bars arranged in a circle. Each bar extends outward from a central
 * ring, like a radial equalizer. The circle slowly rotates over time.
 *
 * HOW IT WORKS:
 * 1. We arrange `barCount` bars evenly around 360°.
 * 2. Each bar's length = frequency amplitude at that bin.
 * 3. Bars are drawn as lines radiating outward from a central radius.
 * 4. A slow rotation is applied so the whole thing spins gently.
 * 5. Beat detection makes the inner radius pulse outward.
 */

import { Visualization, FrameData, VisualizationSetting } from '../../types';
import { BeatPulse, SmoothedValue } from '../../utils/audioUtils';
import { hsl } from '../../utils/colorUtils';

export class CircularSpectrum implements Visualization {
  id = 'circular-spectrum';
  name = 'Circular Spectrum';
  category = 'spectrum' as const;
  description = 'Radial frequency bars arranged in a spinning circle';
  renderer = 'canvas2d' as const;

  private canvas!: HTMLCanvasElement;
  private ctx!: CanvasRenderingContext2D;
  private beatPulse = new BeatPulse(0.91);
  private smoothRadius = new SmoothedValue(0.08);
  private rotation = 0;

  private barCount = 90;
  private baseRadius = 0.2;  // fraction of min(width, height)/2
  private rotationSpeed = 0.3;
  private colorHue = 180;

  init(canvas: HTMLCanvasElement): void {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d')!;
  }

  render(frame: FrameData): void {
    const { frequencyData, isBeat, beatIntensity, deltaTime } = frame;
    const { width, height } = this.canvas;
    const ctx = this.ctx;
    const cx = width / 2;
    const cy = height / 2;
    const minDim = Math.min(width, height);

    this.beatPulse.update(isBeat, beatIntensity);

    // The inner radius pulses outward on beats
    const radiusPulse = this.smoothRadius.update(this.baseRadius + this.beatPulse.value * 0.06);
    const innerRadius = radiusPulse * minDim / 2;
    const maxBarLength = (minDim / 2) - innerRadius - 10;

    // Slow rotation
    this.rotation += (this.rotationSpeed * deltaTime) / 1000;

    // Fade to black with trail
    ctx.fillStyle = 'rgba(0, 0, 0, 0.88)';
    ctx.fillRect(0, 0, width, height);

    const usableBins = Math.floor(frequencyData.length * 0.5);
    const binsPerBar = usableBins / this.barCount;
    const angleStep = (Math.PI * 2) / this.barCount;

    ctx.lineWidth = Math.max(1, (Math.PI * 2 * innerRadius) / this.barCount * 0.6);
    ctx.lineCap = 'round';

    for (let i = 0; i < this.barCount; i++) {
      // Average bins for this bar
      const startBin = Math.floor(i * binsPerBar);
      const endBin = Math.floor((i + 1) * binsPerBar);
      let sum = 0;
      for (let b = startBin; b < endBin; b++) sum += frequencyData[b];
      const amplitude = sum / (endBin - startBin) / 255;

      const barLength = amplitude * maxBarLength * 1.3;
      const angle = i * angleStep + this.rotation;

      // Start point (on the inner circle)
      const x1 = cx + Math.cos(angle) * innerRadius;
      const y1 = cy + Math.sin(angle) * innerRadius;
      // End point (extending outward)
      const x2 = cx + Math.cos(angle) * (innerRadius + barLength);
      const y2 = cy + Math.sin(angle) * (innerRadius + barLength);

      // Color: hue wraps around the circle
      const hue = (i / this.barCount) * 360 + this.colorHue;
      const lightness = 45 + this.beatPulse.value * 20;
      ctx.strokeStyle = hsl(hue, 80, lightness);

      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();
    }

    // Draw inner circle outline
    ctx.beginPath();
    ctx.arc(cx, cy, innerRadius, 0, Math.PI * 2);
    ctx.strokeStyle = hsl(this.colorHue, 60, 40, 0.5);
    ctx.lineWidth = 1;
    ctx.stroke();
  }

  resize(width: number, height: number): void {
    this.canvas.width = width;
    this.canvas.height = height;
  }

  destroy(): void {}

  getSettings(): VisualizationSetting[] {
    return [
      { key: 'barCount', label: 'Bars', type: 'range', value: this.barCount, min: 30, max: 180, step: 10 },
      { key: 'baseRadius', label: 'Radius', type: 'range', value: this.baseRadius, min: 0.1, max: 0.4, step: 0.02 },
      { key: 'rotationSpeed', label: 'Spin Speed', type: 'range', value: this.rotationSpeed, min: 0, max: 2, step: 0.1 },
      { key: 'colorHue', label: 'Color Shift', type: 'range', value: this.colorHue, min: 0, max: 360, step: 10 },
    ];
  }

  setSettings(settings: Record<string, number | string | boolean>): void {
    if (typeof settings.barCount === 'number') this.barCount = settings.barCount;
    if (typeof settings.baseRadius === 'number') this.baseRadius = settings.baseRadius;
    if (typeof settings.rotationSpeed === 'number') this.rotationSpeed = settings.rotationSpeed;
    if (typeof settings.colorHue === 'number') this.colorHue = settings.colorHue;
  }
}
